// Payload's relationship structure
interface InternalLinkValue {
  relationTo?: string;
  value?: string | { slug?: string | null } | null;
}

// Type that represents the structure created by createLinkField
// Using a more permissive type to handle various Payload structures
type LinkFieldStructure<TFieldName extends string> = {
  [K in `${TFieldName}Type`]?: ("internal" | "external") | null;
} & {
  [K in `${TFieldName}Internal`]?: InternalLinkValue | null;
} & {
  [K in TFieldName]?: string | null;
} & Record<string, unknown>; // Allow additional properties

/**
 * Resolves a link URL from the link field
 * The field stores either a string (external URL) or an object (internal page)
 *
 * @param data - The data object containing the link field
 * @param fieldName - The base field name (e.g., "primaryButtonLink")
 * @returns The resolved URL string or undefined
 */
export const resolveLinkUrl = <TFieldName extends string>(
  data: LinkFieldStructure<TFieldName> | null | undefined,
  fieldName: TFieldName,
): string | undefined => {
  if (!data) return undefined;

  const linkType = data[`${fieldName}Type`];

  if (linkType === "internal") {
    const internalField = data[`${fieldName}Internal`] as
      InternalLinkValue | undefined;

    const value = internalField?.value;
    const slug = typeof value === "object" ? value?.slug : undefined;

    return slug?.startsWith("/") ? slug : `/${slug}`;
  }

  // For external links or when type is not "internal" (for backward compatibility and anchorlinks)
  const value = data[fieldName];
  return typeof value === "string" ? value : undefined;
};
